import React, { useEffect, useState } from "react";
import "./SocialMedia.css";

import {
    GetLinkStatus,
    LinkGoogle, UnlinkGoogle,
    LinkTikTok, UnlinkTikTok,
    LinkFacebook, UnlinkFacebook,
    LinkInstagram, UnlinkInstagram,
    CompleteChannelLink
} from "../../Client/ManagementServiceClient.js";
import { getAuth } from "firebase/auth";
import { isPublishingAvailable } from "./channelAvailability";

const CHANNEL_ACTIONS = {
    Youtube: { link: LinkGoogle, unlink: UnlinkGoogle },
    TikTok: { link: LinkTikTok, unlink: UnlinkTikTok },
    Facebook: { link: LinkFacebook, unlink: UnlinkFacebook },
    Instagram: { link: LinkInstagram, unlink: UnlinkInstagram },
};

// Channels we support linking. The buttons always render in this order; the
// backend link status only overlays the connected/not-connected state.
const ALL_CHANNELS = ['Youtube', 'TikTok', 'Facebook', 'Instagram'];
const SUPPORTED_CHANNELS = ALL_CHANNELS.filter(isPublishingAvailable);

function SocialMedia() {
    const [accounts, setAccounts] = useState(
        SUPPORTED_CHANNELS.map((name) => ({ name, connected: false }))
    );
    const [linkError, setLinkError] = useState('');

    useEffect(() => {
        async function fetchLinkStatus(idToken) {
            const { status, myJson } = await GetLinkStatus(idToken);
            if (status === 200) {
                const statusByName = {};
                const linkedChannels = myJson.LinkedChannels || [];
                for (let i = 0; i < linkedChannels.length; i++) {
                    statusByName[linkedChannels[i].Channel] = linkedChannels[i];
                }

                // Keep the full supported list visible, overlaying status. A
                // channel no longer offered still shows while it is linked, so
                // the account can be unlinked.
                const shown = ALL_CHANNELS.filter(name => (
                    isPublishingAvailable(name) || statusByName[name]?.Linked
                ));
                setAccounts(shown.map((name) => ({
                    name,
                    connected: Boolean(statusByName[name]?.Linked),
                    // Meta access expires ~60 days after linking and can only be
                    // renewed while still valid; past that the account looks
                    // connected but every post fails until it is reconnected.
                    needsReconnect: Boolean(statusByName[name]?.NeedsReconnect),
                })));
            }
        }

        // Returning from an OAuth consent screen, the callback hands us a
        // one-time token instead of having linked anything. Claiming it here --
        // as the signed-in user -- is what completes the link, and it only
        // succeeds if this is the same account that started the flow.
        async function claimPendingLink(idToken) {
            const params = new URLSearchParams(window.location.search);
            const linkToken = params.get('link_token');
            const callbackError = params.get('link_error');
            const channel = params.get('channel');
            if (!linkToken && !callbackError) {
                return;
            }

            // Drop these from the URL either way so a refresh or a shared link
            // can't replay the token.
            params.delete('link_token');
            params.delete('channel');
            params.delete('link_error');
            const query = params.toString();
            window.history.replaceState(
                {}, '', window.location.pathname + (query ? `?${query}` : '')
            );

            if (!linkToken) {
                setLinkError(
                    callbackError === 'scopes'
                        ? `${channel || 'That account'} was not connected because some requested permissions were declined. Please try again and accept all of them.`
                        : 'Could not finish connecting that account. Please try again.'
                );
                return;
            }

            const { status, myJson } = await CompleteChannelLink(idToken, linkToken);
            if (status !== 200) {
                setLinkError(myJson?.detail || 'Could not finish connecting that account. Please try again.');
            }
        }

        async function load() {
            try {
                const auth = getAuth();
                const user = auth.currentUser;
                if (!user) {
                    console.error("No user is signed in.");
                    return;
                }
                const idToken = await user.getIdToken();
                await claimPendingLink(idToken);
                await fetchLinkStatus(idToken);
            } catch (error) {
                console.error("Failed to load social media accounts:", error);
            }
        }

        load();
    }, []);

    const handleLink = async (accountName) => {
        try {
            const auth = getAuth();
            const user = auth.currentUser;
            if (user) {
                const idToken = await user.getIdToken();

                const actions = CHANNEL_ACTIONS[accountName];
                if (!actions) {
                    console.error(`Unsupported account type: ${accountName}`);
                    return;
                }
                const response = await actions.link(idToken);

                const { status, myJson } = response;
                if (status === 200) {
                    const redirectUrl = myJson.RedirectUrl;
                    if (redirectUrl) {
                        window.location.href = redirectUrl;
                    }
                }
            } else {
                console.error("No user is signed in.");
            }
        } catch (error) {
            console.error(`Failed to link ${accountName} account:`, error);
        }
    };

    const handleUnlink = async (accountName) => {
        try {
            const auth = getAuth();
            const user = auth.currentUser;
            if (user) {
                const idToken = await user.getIdToken();

                const actions = CHANNEL_ACTIONS[accountName];
                if (!actions) {
                    console.error(`Unsupported account type: ${accountName}`);
                    return;
                }
                const response = await actions.unlink(idToken);

                const { status } = response;
                if (status === 200) {
                    // Update the account state to reflect the new connection status
                    setAccounts(accounts.map(account => 
                        account.name === accountName ? { ...account, connected: false } : account
                    ));
                }
            } else {
                console.error("No user is signed in.");
            }
        } catch (error) {
            console.error(`Failed to unlink ${accountName} account:`, error);
        }
    };

    // Linking is refused while a credential doc still exists, so reconnecting an
    // expired account has to clear the stale one before starting consent again.
    const handleReconnect = async (accountName) => {
        try {
            const auth = getAuth();
            const user = auth.currentUser;
            if (!user) {
                console.error("No user is signed in.");
                return;
            }
            const idToken = await user.getIdToken();
            const actions = CHANNEL_ACTIONS[accountName];
            if (!actions) {
                console.error(`Unsupported account type: ${accountName}`);
                return;
            }
            await actions.unlink(idToken);
            await handleLink(accountName);
        } catch (error) {
            console.error(`Failed to reconnect ${accountName} account:`, error);
            setLinkError(`Could not reconnect ${accountName}. Please unlink it and connect again.`);
        }
    };

    return (
        <div className="social-media-container">
            <h1>Social accounts</h1>
            <p>Connect your social media accounts</p>
            {linkError && (
                <div className="social-media-error" role="alert">
                    {linkError}
                    <button onClick={() => setLinkError('')} aria-label="Dismiss">×</button>
                </div>
            )}
            {accounts.map((account) => (
                <div className="social-media-account" key={account.name} data-platform={account.name}>
                    <div className="account-info">
                        <h2>{account.name}</h2>
                        <p className={`status ${account.needsReconnect ? "needs-reconnect" : account.connected ? "connected" : "not-connected"}`}>
                            <span className="status-indicator"></span>
                            {account.needsReconnect
                                ? "Reconnect needed — access expired"
                                : account.connected ? "Connected" : "Not Connected"}
                        </p>
                    </div>
                    <div className="account-actions">
                        {account.needsReconnect ? (
                            <button onClick={() => handleReconnect(account.name)}>Reconnect {account.name} Account</button>
                        ) : account.connected ? (
                            <button onClick={() => handleUnlink(account.name)}>Unlink {account.name} Account</button>
                        ) : (
                            <button onClick={() => handleLink(account.name)}>Link {account.name} Account</button>
                        )}
                    </div>
                </div>
            ))}
        </div>
    );
}

export { SocialMedia };